import { IsString, IsOptional, IsEmail, IsUrl, IsNumber, IsBoolean, IsNotEmpty, Min, Max, Validate, ValidateIf } from 'class-validator';
import { ConnectorConfig } from '../../../models';
import { IsCredentialFreeDataSourceUrl } from '../../../data-source-url.validator';

export class ConnectorConfigBrandshare extends ConnectorConfig {
	/**
	 * List ID in Brandshare - the unique identifier for the content collection
	 */
	@IsString()
	@IsNotEmpty()
	target: string;

	/**
	 * API key for Brandshare authentication (optional - can use environment variable)
	 */
	@IsOptional()
	@IsString()
	apiKey?: string;

	/**
	 * Base URL for Brandshare API (defaults to official instance)
	 */
	@IsOptional()
	@IsString()
	@ValidateIf(o => o.baseUrl && o.baseUrl.trim() !== '')
	@IsUrl({ protocols: ['https'], require_protocol: true })
	@Validate(IsCredentialFreeDataSourceUrl)
	baseUrl?: string = 'https://cce.brandsharedam.com';

	/**
	 * Number of days before download links expire (1-365)
	 */
	@IsOptional()
	@IsNumber()
	@Min(1)
	@Max(365)
	linkExpireInDays?: number = 7;

	/**
	 * Request timeout in milliseconds
	 */
	@IsOptional()
	@IsNumber()
	@Min(1000)
	@Max(60000)
	timeout?: number = 30000;

	/**
	 * Whether to load full folder tree structure
	 */
	@IsOptional()
	@IsBoolean()
	loadFullFolderTree?: boolean = false;

	/**
	 * Whether to automatically save selected assets to the library
	 */
	@IsOptional()
	@IsBoolean()
	saveToLibrary?: boolean = true;
}

// Brandshare API response models
export interface BrandshareFilterItem {
	Id: string;
	Value?: string;
	Values?: string[];
}

export interface BrandshareSearchParams {
	listId: string;
	searchTerm?: string;
	filter?: BrandshareFilterItem[];
	sortBy?: string;
	sortDirection?: 'asc' | 'desc';
	page?: number;
	pageSize?: number;
	// Note: folder filtering goes through `filter` as a Filter[] entry keyed
	// by the per-list "Folder" field ID. Top-level FolderId is silently
	// ignored by Brandshare's Search API and is intentionally not modeled
	// here so callers can't reach for it by mistake.
	fileType?: string;
	excludeExpiredItems?: boolean;
}

export interface BrandshareSearchResponse {
	TotalCount: number;
	FormContents: {
		Fields: any[];
		Contents: any[];
		CS: any;
	};
	FieldIds: string[];
}

export interface BrandshareItem {
	id: string;
	title?: string;
	fileName?: string;
	description?: string;
	path?: string;
	fileSize?: number;
	fileSizeBytes?: number;
	fileType?: string;
	extension?: string;
	thumbnailUrl?: string;
	createdDate?: string;
	modifiedDate?: string;
	folderId?: string;
	folderPath?: string;
	customFields?: Record<string, any>;
	metadata?: Record<string, any>;
}

export interface BrandshareDownloadLink {
	Url: string;
	FileName: string;
}

export interface BrandshareVolume {
	id: string;
	name: string;
	path: string;
	parentId?: string;
	type?: string;
}

export interface BrandshareFolder {
	id: string;
	name: string;
	path: string;
	parentId?: string;
	children?: BrandshareFolder[];
}

export interface BrandshareForm {
	id: string;
	name: string;
	fields: BrandshareFormField[];
	folderTree?: any;
}

export interface BrandshareFormField {
	id: string;
	name: string;
	type: string;
	required: boolean;
	options?: any[];
}

export interface BrandshareCreateItemRequest {
	listId: string;
	title: string;
	description?: string;
	folderPath?: string;
	metadata?: Record<string, any>;
	fileName?: string;
}

export interface BrandshareCreateItemResponse {
	id: string;
	title: string;
	path: string;
	success: boolean;
	message?: string;
}

export interface BrandshareEditMetadataRequest {
	itemId: string;
	fieldValues: Record<string, any>;
}

export interface BrandshareEditMetadataResponse {
	success: boolean;
	message?: string;
}
